import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { useSignedInUserState } from "../../hooks/useAuthState";
import { restaurantService } from "../../services/firebase/restaurantManagementService";
import { staffService } from "../../services/firebase/staffService";
import { StringsService } from "../../services/strings/strings";
import { FullProperty, emptyProperty } from "../../models/fullProperty";
import AsyncCardMedia from "../../shared/asyncCardMedia";
import AsyncString from "../../shared/asyncString";
import InviteStaffDialog from "../staffInvitationModal";
import { ConfirmActionDialog } from "../../shared/confirmActionDialog";
import { CreateStaffDialog } from "../createStaffModal";
import { StaffMappingService } from "../../services/profile/staffMapping";

interface staffViewData {
  id: string;
  name: string;
  email: string;
}

const SingleRestaurantStaffManagement: React.FC<{ restaurant: FullProperty; updateRestaurant: () => Promise<void> }> = ({ restaurant, updateRestaurant }) => {
  const [staffList, setStaffList] = useState<staffViewData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const { user, profile } = useSignedInUserState();
  const navigate = useNavigate();
  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  const [createStaffDialogOpen, setCreateStaffDialogOpen] = useState<boolean>(false);
  const [confirmRemoveDialog, setConfirmRemoveDialog] = useState<{ open: boolean; staffId?: string }>({ open: false });

  const fetchRestaurantData = async () => {
    try {
      if (!user || !user.uid || !profile) {
        setError(await StringsService.getStringById("error-not-signed-in"));
        setLoading(false);
        return;
      }
      setLoading(true);
      console.log("user, profile", user, profile)

      const staff = await restaurantService.fetchStaffByRestaurant(restaurant);
      const customStaff = await StaffMappingService.getStaffUnderOwner(profile.email)
      const viewData = staff.map((s) => {
        if (s.id in customStaff) {
          return {
            id: s.id,
            email: s.name,
            name: customStaff[s.id].username
          } as staffViewData
        }
        return {
          id: s.id,
          email: s.email,
          name: s.name
        } as staffViewData
      })
      console.log("SingleRestaurantStaffManagement, staff:", staff)
      console.log("SingleRestaurantStaffManagement, customStaff:", customStaff)
      console.log("SingleRestaurantStaffManagement, viewData:", viewData)

      setStaffList(viewData);
    } catch (err) {
      console.error("SingleRestaurantStaffManagement, err:", err)
      setError("Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurantData();
  }, [user, profile, restaurant]);

  const handleRemoveStaff = async () => {
    if (!confirmRemoveDialog.staffId || !restaurant) return;

    try {
      await staffService.removeStaffFromRestaurant(confirmRemoveDialog.staffId, restaurant);
      setStaffList((prev) => prev.filter((staff) => staff.id !== confirmRemoveDialog.staffId));
      setConfirmRemoveDialog({ open: false });
    } catch (error) {
      setError("Failed to remove staff member. Please try again later.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }


  return (
    <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", py: 4 }} className={"patlite"}>
      <Card sx={{ maxWidth: 600, width: "100%", p: 3, boxShadow: 3 }}>
        <AsyncCardMedia component="img" height="200" imageId={`${restaurant.id}-thumbnail`} alt="Restaurant Thumbnail" />
        <CardContent>
          <Typography variant="h5" fontWeight="bold" textAlign="center">
            {restaurant.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
            <AsyncString stringId={`${restaurant.id}-short-restaurant-summary`} />
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6">Staff Members</Typography>
          {staffList.length > 0 ? (
            <List>
              {staffList.map((staff) => (
                <ListItem key={staff.id} secondaryAction={
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => setConfirmRemoveDialog({ open: true, staffId: staff.id })}
                  >
                    <CloseIcon />
                  </IconButton>
                }>
                  <ListItemText primary={staff.name} secondary={staff.email} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No staff members have been added yet.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: "space-between", mt: 2 }}>
            <Button variant="outlined" onClick={() => setInviteDialogOpen(true)}
              sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
            >
              <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>

                Invite Existing User
              </Typography>

            </Button>

            <Button variant="outlined" onClick={() => setCreateStaffDialogOpen(true)}
              sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
            >
              <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                Create Staff
              </Typography>
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Invite Staff Dialog */}
      {user && (
        <InviteStaffDialog sentBy={user.uid} open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} restaurant={restaurant} />
      )}

      {/* Create Staff Dialog */}
      {profile && (
        <CreateStaffDialog
          onSuccess={async () => {
            setLoading(true)
            setTimeout(async () => {
              await updateRestaurant()
            }, 2000)
            }}
            sentBy = { profile }
            open = { createStaffDialogOpen }
            onClose = {() => setCreateStaffDialogOpen(false)} restaurant={restaurant} />
      )}

      {/* Confirm Remove Staff Dialog */}
      <ConfirmActionDialog
        isOpen={confirmRemoveDialog.open}
        onClose={() => setConfirmRemoveDialog({ open: false })}
        onConfirm={handleRemoveStaff}
        title="Confirm Removal"
      >
        Are you sure you want to remove this staff member?
      </ConfirmActionDialog>
    </Box>
  );
};

export default SingleRestaurantStaffManagement;
